import { normalizeConfig, BORLABS_SERVICE_MAP, type ConsentConfig } from './types';

/**
 * Borlabs compatibility shim. Borlabs' GTM "Consent Variable" template calls
 * BorlabsCookie.checkCookieConsent(key) / BorlabsCookie.Consents.hasConsent(key).
 * We emulate that by reading the CookieConsent (cc_cookie) state, so existing GTM
 * containers built for Borlabs keep working unchanged after the swap. Defined
 * synchronously in <head> so it's available before gtm.js and DOM_READY.
 *
 * Resolution order for hasConsent('<key>'):
 *   1. necessary services are always granted;
 *   2. service-level: accepted in cc_cookie.services -> granted;
 *   3. configured-but-not-accepted service -> denied (honours per-service opt-out);
 *   4. unconfigured key -> fall back to its mapped category.
 *
 * @param serviceKeys keys configured as individual services (from config.services)
 */
export function borlabsCompatSnippet(serviceKeys: string[] = []): string {
  return `(function(){var m=${JSON.stringify(BORLABS_SERVICE_MAP)},svc=${JSON.stringify(serviceKeys)};
function has(s){if(m[s]==='necessary')return true;try{var p=document.cookie.split('; '),d=null;for(var i=0;i<p.length;i++){if(p[i].indexOf('cc_cookie=')===0){d=JSON.parse(decodeURIComponent(p[i].substring(10)));break;}}if(!d)return false;var list=[];if(d.services){for(var k in d.services){if(d.services[k])list=list.concat(d.services[k]);}}if(list.indexOf(s)!==-1)return true;if(svc.indexOf(s)!==-1)return false;var c=m[s];if(!c)return false;return (d.categories||[]).indexOf(c)!==-1;}catch(e){return false;}}
window.BorlabsCookie=window.BorlabsCookie||{};window.BorlabsCookie.Consents=window.BorlabsCookie.Consents||{};window.BorlabsCookie.Consents.hasConsent=has;window.BorlabsCookie.checkCookieConsent=has;})();`;
}

/**
 * Restore a stored decision (cc_cookie) into Consent Mode synchronously, BEFORE the
 * GTM loader. Mirror of syncConsentMode() in init.ts — keep the category mapping in
 * sync. Also mirrored verbatim in the WP plugin (class-consent.php, render_head).
 *
 * Why here and not in the banner bundle: GTM fires its "Consent Initialization" and
 * "Initialization" triggers before it processes dataLayer entries queued AFTER the
 * loader's `gtm.js` marker, and tags with additional consent checks are evaluated
 * exactly once — a later update never re-fires them. The bundle's update (at
 * DOMContentLoaded) therefore arrives too late for Initialization tags (e.g.
 * Nimbata call tracking); the fleet's containers rely on that trigger. Borlabs did
 * the same thing (its head snippet merges `borlabs-cookie-gcs` into the default).
 * No decision stored → nothing is pushed and the denied default stands.
 */
export function restoreConsentSnippet(): string {
  return `(function(){try{var p=document.cookie.split('; '),d=null;for(var i=0;i<p.length;i++){if(p[i].indexOf('cc_cookie=')===0){d=JSON.parse(decodeURIComponent(p[i].substring(10)));break;}}if(!d||!d.categories||!d.categories.length)return;var c=d.categories,g=function(k){return c.indexOf(k)!==-1?'granted':'denied';},a=g('analytics'),s=g('ads'),f=g('functional');gtag('consent','update',{analytics_storage:a,ad_storage:s,ad_user_data:s,ad_personalization:s,functionality_storage:f,personalization_storage:f});}catch(e){}})();`;
}

/**
 * Returns the JavaScript that MUST run as early as possible in <head>, before any
 * tag fires. It is intentionally framework-agnostic: it returns the inner JS only
 * (no <script> tags) so each adapter can wrap it (`echo "<script>...";` in WP,
 * `<script is:inline set:html={...}>` in Astro).
 *
 * Responsibilities, in order:
 *  1. dataLayer + gtag stub
 *  2. Consent Mode v2 default = denied (advanced mode: GTM still loads, tags wait)
 *  3. ads_data_redaction + url_passthrough (recommended privacy defaults)
 *  4. Borlabs compatibility shim (so existing GTM containers keep working)
 *  5. Stored decision → consent update (returning visitors, see restoreConsentSnippet)
 *  6. GTM loader (only when a valid container id is present)
 */
export function headSnippet(input: Partial<ConsentConfig>): string {
  const { gtmId, services } = normalizeConfig(input);
  const shim = borlabsCompatSnippet((services ?? []).map((s) => s.key));
  const restore = restoreConsentSnippet();

  const bootstrap = `
window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('consent','default',{
  ad_storage:'denied',
  analytics_storage:'denied',
  ad_user_data:'denied',
  ad_personalization:'denied',
  functionality_storage:'denied',
  personalization_storage:'denied',
  security_storage:'granted',
  wait_for_update:500
});
gtag('set','ads_data_redaction',true);
gtag('set','url_passthrough',true);`.trim();

  if (!gtmId) return `${bootstrap}\n${shim}\n${restore}`;

  const gtmLoader = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${gtmId}');`;

  return `${bootstrap}\n${shim}\n${restore}\n${gtmLoader}`;
}

/**
 * The GTM <noscript> fallback markup for <body> (Astro/WP both place it right after
 * <body>). Returns an empty string when no valid container id is configured.
 */
export function noscriptSnippet(input: Partial<ConsentConfig>): string {
  const { gtmId } = normalizeConfig(input);
  if (!gtmId) return '';
  return `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
}
